import { useEventListener } from '@cheqroom/hooks';
import { Button, ButtonGroup, Modal, Text } from '@cheqroom/ui';
import { type Variation } from '@cheqroom/ui/Button';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useConfirmation from '../confirmation/useConfirmation';

let pendingNavigationDeactivateBypass = false;

/** Skip the next `window.onDeactivate` confirmation once (e.g. workflow navigate with details.confirm false). */
export function requestNavigationDeactivateBypassOnce(): void {
	pendingNavigationDeactivateBypass = true;
}

function consumeNavigationDeactivateBypass(): boolean {
	if (!pendingNavigationDeactivateBypass) {
		return false;
	}
	pendingNavigationDeactivateBypass = false;
	return true;
}

/**
 * @deprecated Use useNavigate instead
 */
export const useNavigation = <Context,>({
	canDeactivate,
	getModal,
	getContext,
}: {
	canDeactivate?: boolean | (() => boolean);
	getContext?: () => Promise<Context> | Context;
	getModal?: (context?: Context) => {
		title: string;
		body: string;
		/** When true, render a ✕ / Esc close affordance that cancels navigation (stay on page). Opt-in per variant. */
		closable?: boolean;
		actions: {
			primary: {
				text: string;
				onClick?: () => void;
				variation?: Variation;
				behaviour: 'confirm' | 'cancel';
			};
			secondary: {
				text: string;
				onClick?: () => void;
				variation?: Variation;
				behaviour: 'confirm' | 'cancel';
			};
		};
	};
} = {}) => {
	const { t } = useTranslation('navigate-warning-modal');
	const [forceAllowDeactivate, setForceAllowDeactivate] = useState(false);
	const forceAllowDeactivateRef = useRef(false);

	const navigate = (path: string, args?: { replace: boolean; trigger: boolean }) => {
		forceAllowDeactivateRef.current = true;
		setForceAllowDeactivate(true);
		window.app.router.navigate(path, args);
	};

	const navigateBack = () => window.app.router.navigateBack();

	const hasDeactivateHandler = canDeactivate !== undefined;

	useEffect(() => {
		// Only reset window.onDeactivate when a component WITH canDeactivate unmounts
		// This prevents components using useNavigation() just for navigate() from
		// accidentally resetting the deactivation handler set by another component
		if (!hasDeactivateHandler) {
			return;
		}

		return () => {
			window.onDeactivate = () => Promise.resolve(true);
		};
	}, [hasDeactivateHandler]);

	const canDeactivateResult = typeof canDeactivate === 'function' ? canDeactivate() : canDeactivate;
	const shouldBlockDeactivation = canDeactivate !== undefined && !canDeactivateResult && !forceAllowDeactivate;

	useEventListener(
		'beforeunload',
		(event) => {
			event.preventDefault();
		},
		shouldBlockDeactivation
	);

	const [showDeactivateConfirmationModal] = useConfirmation<{ context: Awaited<Context> | undefined }>(
		({ onCancel, onConfirm, onClose, isShown, context }) => {
			const modal = getModal?.(context);
			const closable = modal?.closable ?? false;

			const handleSecondaryClick = async () => {
				if (modal?.actions?.secondary.behaviour === 'confirm') {
					await onConfirm();
				} else {
					await onCancel();
				}
				modal?.actions?.secondary?.onClick?.();
			};

			const handlePrimaryClick = async () => {
				if (modal?.actions?.primary.behaviour === 'cancel') {
					await onCancel();
				} else {
					await onConfirm();
				}
				modal?.actions?.primary?.onClick?.();
			};

			return (
				<Modal
					open={isShown}
					onClose={closable ? onClose : undefined}
					closeOnEsc={closable}
					header={
						<Modal.Header onClose={closable ? onClose : undefined}>
							{modal?.title || t('header.title')}
						</Modal.Header>
					}
					footer={
						<Modal.Footer>
							<ButtonGroup>
								<Button
									variation={modal?.actions?.secondary.variation ?? 'secondary'}
									onClick={handleSecondaryClick}
								>
									{modal?.actions?.secondary?.text || t('footer.confirm')}
								</Button>
								<Button
									variation={modal?.actions?.primary.variation ?? 'destructive'}
									onClick={handlePrimaryClick}
								>
									{modal?.actions?.primary?.text || t('footer.cancel')}
								</Button>
							</ButtonGroup>
						</Modal.Footer>
					}
				>
					<Modal.Body>
						<Modal.Section>
							<Text>{modal?.body || t('body.description')}</Text>
						</Modal.Section>
					</Modal.Body>
				</Modal>
			);
		}
	);

	const deactivate = async (): Promise<boolean> => {
		if (forceAllowDeactivateRef.current) {
			forceAllowDeactivateRef.current = false;
			return true;
		}

		if (consumeNavigationDeactivateBypass()) {
			return true;
		}

		const context = getContext ? await getContext() : undefined;
		if (shouldBlockDeactivation) {
			return new Promise((resolve) =>
				showDeactivateConfirmationModal(() => resolve(true), {
					onClose: () => resolve(false),
					onCancel: () => resolve(false),
					context,
				})
			);
		}

		return true;
	};

	// Only set onDeactivate when canDeactivate is explicitly set. Otherwise it will unintentionally implicitly overrule explicitly set behaviour.
	if (canDeactivate !== undefined) {
		window.onDeactivate = deactivate;
	}

	return { navigate, navigateBack };
};

export default useNavigation;
